import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { createElement } from "react";
import { Link as RouterLink } from "react-router-dom";

import { MASCOT_PALETTE } from "@/components/Others/MascotPanel";
import { colors } from "@/theme";

import { accentColor, bannerBackgroundSx, borderColor, isDarkTheme } from "../../utils/matchOdds";

/* A match page: the hero, its score and period table, the sticky strip, the breadcrumb. */

/**
 * The hero of a match page: the sport photo under a veil, so the card on it
 * reads: a light one on the light page, the night sky on the dark one.
 */
export const BannerBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "backgroundUrl",
})<{ backgroundUrl: string }>(({ theme, backgroundUrl }) => ({
  borderRadius: "12px",
  overflow: "hidden",
  ...bannerBackgroundSx(backgroundUrl),
  backgroundImage: `linear-gradient(180deg, ${alpha(colors.white, 0.18)}, ${alpha(MASCOT_PALETTE.night, 0.45)}), url(${backgroundUrl})`,
  padding: 12,
  ...theme.applyStyles("dark", {
    backgroundImage: `linear-gradient(180deg, ${alpha(MASCOT_PALETTE.night, 0.35)}, ${alpha(MASCOT_PALETTE.night, 0.72)}), url(${backgroundUrl})`,
  }),
  "@media (min-width: 900px)": {
    padding: 40,
  },
}));

/**
 * The card on the hero: cloud white on the light page; on the dark one the
 * night, with the blue light from below that the mascot stands in.
 */
export const InfoCard = styled(Stack)(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  padding: 16,
  backgroundColor: alpha(colors.white, 0.9),
  border: `1px solid ${alpha(colors.prime, 0.28)}`,
  boxShadow: `0 12px 32px ${alpha(MASCOT_PALETTE.night, 0.25)}`,
  backdropFilter: "blur(6px)",
  ...theme.applyStyles("dark", {
    backgroundColor: alpha(MASCOT_PALETTE.night, 0.86),
    backgroundImage: `radial-gradient(120% 90% at 50% 120%, ${alpha(colors.prime, 0.3)}, transparent 60%)`,
    border: `1px solid ${alpha(MASCOT_PALETTE.sky, 0.3)}`,
  }),
  "@media (min-width: 900px)": {
    padding: "28px 32px",
  },
}));

export const LeagueTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: accentColor(theme),
}));

/** The score on the hero: two figures with a quiet colon, the side in front in the crown gold; `compact` on the strip. */
export const ScoreLine = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ compact }) => ({
  flexDirection: "row",
  alignItems: "baseline",
  gap: compact ? 6 : 10,
  paddingLeft: compact ? 8 : 16,
  paddingRight: compact ? 8 : 16,
}));

export const ScoreFigure = styled("span", {
  shouldForwardProp: (prop) => prop !== "leading" && prop !== "compact",
})<{ leading: boolean; compact?: boolean }>(({ theme, leading, compact }) => {
  const dark = isDarkTheme(theme);

  return {
    fontSize: compact ? 18 : 34,
    lineHeight: 1,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    color: dark ? MASCOT_PALETTE.sky : accentColor(theme),
    ...(leading && { color: dark ? MASCOT_PALETTE.crown : "#6B4A00" }),
  };
});

export const ScoreColon = styled("span", {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ theme, compact }) => ({
  fontSize: compact ? 14 : 22,
  fontWeight: 700,
  color: theme.palette.text.secondary,
}));

/** Scrolls the period table sideways on a narrow phone instead of the page. */
export const PeriodTableWrap = styled("div")({
  width: "100%",
  maxWidth: 520,
  margin: "0 auto",
  overflowX: "auto",
});

/** The score of each set / quarter / period under the hero score: a side's name, then one column per period. */
export const PeriodGrid = styled("table")({
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "4px 2px",
  fontVariantNumeric: "tabular-nums",
});

export const PeriodHead = styled("th")(({ theme }) => ({
  width: 28,
  padding: 0,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: "18px",
  textAlign: "center",
  whiteSpace: "nowrap",
  color: theme.palette.text.secondary,
}));

/** A side's name: takes what the period columns leave and ends in an ellipsis. */
export const PeriodName = styled("th")(({ theme }) => ({
  width: "100%",
  maxWidth: 0,
  padding: 0,
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "22px",
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: theme.palette.text.secondary,
}));

/** One side's score in one period: bold for the side that took it, sky/accent for the period in play. */
export const PeriodCell = styled("td", {
  shouldForwardProp: (prop) => prop !== "won" && prop !== "current",
})<{ won?: boolean; current?: boolean }>(({ theme, won, current }) => {
  const dark = isDarkTheme(theme);
  return {
    width: 28,
    padding: "0 4px",
    borderRadius: 4,
    fontSize: 13,
    lineHeight: "22px",
    textAlign: "center" as const,
    whiteSpace: "nowrap" as const,
    fontWeight: won ? 700 : 500,
    color: won ? theme.palette.text.primary : theme.palette.text.secondary,
    ...(current && {
      fontWeight: 700,
      color: dark ? MASCOT_PALETTE.sky : accentColor(theme),
      backgroundColor: dark ? alpha(MASCOT_PALETTE.sky, 0.12) : alpha(colors.prime, 0.1),
    }),
  };
});

/**
 * One side on the hero. A row beside the score on a desktop; on a phone a
 * column, the avatar over the name, so the two sides and the score fit in
 * one row and the hero stays short. The away side is a reversed column, to
 * keep its avatar on the outside as on the desktop.
 */
export const TeamSide = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "align",
})<{ align: "start" | "end" }>(({ align }) => ({
  flex: 1,
  minWidth: 0,
  justifyContent: align === "start" ? "flex-start" : "flex-end",
  "@media (max-width: 899.95px)": {
    "&&": {
      flexDirection: align === "start" ? "column" : "column-reverse",
      justifyContent: "flex-start",
      alignItems: "center",
      gap: 6,
      textAlign: "center",
    },
  },
}));

/** The name of a side on the hero: one line beside the avatar on a desktop, two under it on a phone. */
export const SideName = styled(Typography)({
  minWidth: 0,
  fontWeight: 700,
  "@media (min-width: 900px)": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "@media (max-width: 899.95px)": {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    overflowWrap: "anywhere",
    textAlign: "center",
    fontSize: 15,
    lineHeight: 1.25,
  },
});

/**
 * The strip that follows the player down a long page of markets: no height
 * in the flow and pinned to the top of the scroll, so it takes no room while
 * hidden and covers nothing until the hero has gone.
 */
export const StickyScoreRoot = styled(Box)({
  position: "sticky",
  top: 0,
  height: 0,
  zIndex: 3,
  overflow: "visible",
});

export const StickyScoreBar = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ theme, visible }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  top: 8,
  flexDirection: "row",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  borderRadius: 12,
  border: `1px solid ${borderColor(theme)}`,
  background: alpha(colors.white, 0.92),
  backdropFilter: "blur(8px)",
  boxShadow: `0 10px 24px ${alpha(MASCOT_PALETTE.night, 0.18)}`,
  opacity: visible ? 1 : 0,
  transform: visible ? "translateY(0)" : "translateY(-8px)",
  pointerEvents: visible ? "auto" : "none",
  transition: "opacity .2s ease, transform .2s ease",
  ...theme.applyStyles("dark", {
    background: alpha(MASCOT_PALETTE.night, 0.92),
    border: `1px solid ${alpha(MASCOT_PALETTE.sky, 0.3)}`,
    boxShadow: `0 10px 24px ${alpha(MASCOT_PALETTE.night, 0.6)}`,
  }),
  "@media (min-width: 900px)": { gap: 12, padding: "8px 14px" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

/** Text only a screen reader hears, e.g. the polite score announcement. */
export const VisuallyHidden = styled("span")({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});

/** A failed refresh over data still on screen: how old it is and the way to retry. */
export const StaleNotice = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  columnGap: 8,
  rowGap: 4,
  marginBottom: 12,
  padding: "6px 12px",
  borderRadius: 10,
  fontSize: 13,
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
  background: alpha(theme.palette.warning.main, isDarkTheme(theme) ? 0.12 : 0.08),
}));

export const Breadcrumb = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  columnGap: 6,
  rowGap: 2,
  marginBottom: 10,
  flexWrap: "wrap",
  minWidth: 0,
});

/** The way back to the sport: a link, so it takes focus and opens in a new tab. */
export const BreadcrumbLink = styled(RouterLink)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 13,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  textDecoration: "none",
  borderRadius: 4,
  "@media (hover: hover)": { "&:hover": { color: accentColor(theme) } },
  "&:focus-visible": { outline: `2px solid ${accentColor(theme)}`, outlineOffset: 2 },
  // A finger needs the full 44 px even where the text is 13.
  "@media (pointer: coarse)": { minHeight: 44 },
}));

/** A segment's text: one line, cut with an ellipsis when a league name outgrows the row. */
export const BreadcrumbText = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

/** The chevron between segments: decoration, so screen readers skip it. */
export const BreadcrumbSeparator = styled((props: TypographyProps) =>
  createElement(Typography, { component: "span", "aria-hidden": true, ...props }),
)(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  fontSize: 13,
  color: theme.palette.text.disabled,
}));

export const BreadcrumbCurrent = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "anywhere",
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));
